"use client";

import { useCallback, useEffect, useMemo, useState } from "react";

import { ClientInfoLayout } from "@/components/backoffice/cat/vehicle-assignment/client-step/client-info-layout";
import { IndividualsSection } from "@/components/backoffice/cat/vehicle-assignment/client-step/client-individuals-section";
import { Field, FieldContent, FieldLabel } from "@/components/ui/field";
import { profileDisplayValueClassName } from "@/components/backoffice/cat/vehicle-assignment/client-step/party-profile-fields";
import {
  PARTICULIER_SERVER_ERROR_MESSAGE,
  type ParticulierSearchOutcome,
} from "@/lib/cat/client-step-contracts";
import { individualToEditableWithIdentity } from "@/lib/cat/draft-client-mapper";
import { isIndividualEditComplete } from "@/lib/cat/draft-client-mapper";
import type { EditableIndividualWithIdentity } from "@/lib/cat/individual-profile.types";
import type { RetailIndividual } from "@/lib/cat/retail-client.types";
import { useCatParticulierSearchMutation } from "@/lib/queries";

function getAvsCollaboratorStatus(_individual: RetailIndividual): string {
  return "Actif";
}

function AvsClientStep({
  onCanProceedChange,
}: {
  onCanProceedChange?: (canProceed: boolean) => void;
}) {
  const [pEmail, setPEmail] = useState("");
  const [pPhone, setPPhone] = useState("");
  const [pLastName, setPLastName] = useState("");
  const [pFirstName, setPFirstName] = useState("");
  const [particulierOutcome, setParticulierOutcome] =
    useState<ParticulierSearchOutcome | null>(null);
  const [selectedIndividualId, setSelectedIndividualId] = useState<
    string | null
  >(null);
  const [ficheEdit, setFicheEdit] =
    useState<EditableIndividualWithIdentity | null>(null);

  const particulierSearchMutation = useCatParticulierSearchMutation();

  const selectedIndividual = useMemo((): RetailIndividual | null => {
    if (!selectedIndividualId || !particulierOutcome) return null;
    if (particulierOutcome.kind !== "list") return null;
    return (
      particulierOutcome.individuals.find(
        (i) => i.id === selectedIndividualId,
      ) ?? null
    );
  }, [particulierOutcome, selectedIndividualId]);

  const ficheComplete = isIndividualEditComplete(ficheEdit);
  const canProceed =
    Boolean(selectedIndividualId) &&
    Boolean(selectedIndividual) &&
    Boolean(ficheEdit) &&
    ficheComplete &&
    particulierOutcome?.kind === "list" &&
    particulierOutcome.individuals.length > 0;

  useEffect(() => {
    onCanProceedChange?.(canProceed);
  }, [canProceed, onCanProceedChange]);

  const handleSearchClick = useCallback(async () => {
    setSelectedIndividualId(null);
    setFicheEdit(null);
    try {
      const outcome = await particulierSearchMutation.mutateAsync({
        context: "avs",
        email: pEmail.trim(),
        firstName: pFirstName.trim() || undefined,
        lastName: pLastName.trim(),
        phone: pPhone.trim(),
      });
      setParticulierOutcome(outcome);
    } catch (error) {
      console.error("[AvsClientStep] particulier search", error);
      setParticulierOutcome({
        kind: "error",
        message: PARTICULIER_SERVER_ERROR_MESSAGE,
      });
    }
  }, [pEmail, pFirstName, pLastName, pPhone, particulierSearchMutation]);

  const resetParticulierSearch = useCallback(() => {
    setParticulierOutcome(null);
    setSelectedIndividualId(null);
    setFicheEdit(null);
    setPEmail("");
    setPPhone("");
    setPLastName("");
    setPFirstName("");
  }, []);

  const handleSelectIndividual = useCallback(
    (id: string | null) => {
      setSelectedIndividualId(id);
      if (!id || !particulierOutcome || particulierOutcome.kind !== "list") {
        setFicheEdit(null);
        return;
      }
      const individual = particulierOutcome.individuals.find(
        (item) => item.id === id,
      );
      if (!individual) {
        setFicheEdit(null);
        return;
      }
      setFicheEdit(individualToEditableWithIdentity(individual));
    },
    [particulierOutcome],
  );

  return (
    <ClientInfoLayout>
      <IndividualsSection
        idPrefix="avs-collab"
        isSearchLoading={particulierSearchMutation.isPending}
        sectionTitle="Recherche collaborateur AVS"
        criteriaLabel="Critères de recherche AVS"
        emailPlaceholder="Ex: tom.blanc@demo.avs"
        searchButtonLabel="Rechercher un collaborateur AVS"
        resultsLabel={(count) =>
          `${count} résultat(s) AVS — sélectionnez une fiche`
        }
        selectedBadgeLabel="Fiche AVS sélectionnée"
        editBadgeLabel="Modifications AVS en cours"
        editAriaLabelPrefix="Modifier la fiche AVS de"
        noInternalCollaboratorAlert={{
          title: "Blocage AVS",
        }}
        noResultsAlert={{
          title: "Blocage AVS",
          description:
            "Collaborateur introuvable : création impossible dans CAT.",
        }}
        additionalDetails={(individual) => (
          <>
            <Field className="md:col-span-2">
              <FieldLabel>Identifiant collaborateur</FieldLabel>
              <FieldContent>
                <p className={profileDisplayValueClassName}>
                  {individual.employeeId ?? individual.id}
                </p>
              </FieldContent>
            </Field>
            <Field>
              <FieldLabel>Statut</FieldLabel>
              <FieldContent>
                <p className={profileDisplayValueClassName}>
                  {getAvsCollaboratorStatus(individual)}
                </p>
              </FieldContent>
            </Field>
          </>
        )}
        ficheEdit={ficheEdit}
        onFicheChange={setFicheEdit}
        onResetSearch={resetParticulierSearch}
        onSearch={handleSearchClick}
        onSelectIndividual={handleSelectIndividual}
        outcome={particulierOutcome}
        pEmail={pEmail}
        pFirstName={pFirstName}
        pLastName={pLastName}
        pPhone={pPhone}
        selectedIndividual={selectedIndividual}
        selectedIndividualId={selectedIndividualId}
        setPEmail={setPEmail}
        setPFirstName={setPFirstName}
        setPLastName={setPLastName}
        setPPhone={setPPhone}
      />
    </ClientInfoLayout>
  );
}

export { AvsClientStep };
